import React, { useContext } from 'react';
import cx from 'classnames';

import { SKIP_URGENT_MESSAGING_INFO_DIALOG_COOKIE } from 'chat/constants/ChatConstants';
import { SplitComposerModal, UrgentMessagingLearnMoreDialog } from 'chat/components/SplitComposer';
import ButtonStock from 'components/ButtonDeprecated';
import Cookies from 'lib/Cookies';
import Image from 'components/Image';
import LayerTrigger from 'components/LayerTrigger';
import MediaObject from 'components/MediaObject';
import Menu from 'components/Menu';
import MenuButtonStock from 'components/MenuButtonDeprecated';
import ModalMenuItem from 'components/ModalMenuItem';
import ModalTrigger from 'components/ModalTrigger';
import FreeAnnouncementRateLimit from 'chat/components/FreeAnnouncementRateLimit';
import Text from 'components/Text';
import Timestamp from 'components/Timestamp';
import Tooltip from 'components/Tooltip';
import {
  ComposerContext,
  UIEventContext,
} from 'chat/components/SplitComposer/components/ComposerContext';

import type { Props as ButtonProps } from 'components/ButtonDeprecated';
import AnnouncementImage from 'assets/images/SplitComposer/announcement.svg';
import ConversationImage from 'assets/images/SplitComposer/conversation.svg';
import UrgentAnnouncementImage from 'assets/images/SplitComposer/urgent.svg';

import type { OrganizationTarget, GroupTarget } from 'entities/Message';

export type Props = {
  label?: React.ReactNode;
  className?: string | null;
  hideAnnouncements?: boolean | null;
  onComposeSelection?: Function | null;
  initialTarget?: GroupTarget | OrganizationTarget | null;
  canViewUrgentMessaging: boolean;
  canViewConversations: boolean;
  currentUserMutedUntil: Date | undefined | null;
  isRateLimitted?: Boolean;
  full?: boolean;
} & ButtonProps;

export default function TriggerComposerButton(props: Props) {
  const { canViewUrgentMessaging, currentUserMutedUntil } = props;
  if (currentUserMutedUntil) {
    return <Muted {...props} currentUserMutedUntil={currentUserMutedUntil} />;
  }

  if (props.isRateLimitted) {
    return (
      <FreeAnnouncementRateLimit>
        {({ onClick }) => <Button {...props} onClick={onClick} />}
      </FreeAnnouncementRateLimit>
    );
  }

  if (canViewUrgentMessaging || props.canViewConversations) {
    return <MenuButton {...props} />;
  }

  return (
    <ModalTrigger
      modal={
        <SplitComposerModal
          initialTargets={props.initialTarget ? [props.initialTarget] : undefined}
        />
      }
    >
      <Button {...props} />
    </ModalTrigger>
  );
}

export function Muted(props: Props & { currentUserMutedUntil: Date }) {
  const timestamp = <Timestamp date={props.currentUserMutedUntil} format="long" />;
  return (
    <LayerTrigger
      layer={
        <Tooltip>
          <Text align="center" element="div">
            Your messaging privileges have been temporarily suspended until {timestamp}.
          </Text>
        </Tooltip>
      }
      placement="bottom"
    >
      <Button {...props} disabled={true} data-testid="disabled-compose" />
    </LayerTrigger>
  );
}

export function MenuItem(props: { image: string; title: string; subtext: string }) {
  return (
    <MediaObject center gutter="small">
      <Image src={props.image} />
      <Text lineHeight="xlarge">
        <Text element="div" color="chalkboard" weight="semibold" size="medium">
          {props.title}
        </Text>
        <Text color="graphite" size="xsmall">
          {props.subtext}
        </Text>
      </Text>
    </MediaObject>
  );
}

export function Button(props: Props) {
  const { label, ...others } = props;
  return (
    <ButtonStock {...getCommonButtonProps(props)} {...others}>
      {label}
    </ButtonStock>
  );
}

export function MenuButton(props: Props) {
  const { full, label, ...other } = props;
  const composerContext = useContext(ComposerContext);

  const menu = renderMessageTypeMenu({
    ...props,
    uiEventContext: composerContext.getUIEventContext(),
  });

  return (
    <MenuButtonStock {...getCommonButtonProps(props)} {...other} menu={menu} full={full}>
      {label || null}
    </MenuButtonStock>
  );
}

// This is required to be a regular function (as opposed to being a component)
// because it is used with `MenuButtonStock` which is the old
// MenuButton that expect weird things about the nature of the `menu` prop unfortunately
export function renderMessageTypeMenu(
  props: Pick<Props, 'canViewConversations' | 'canViewUrgentMessaging' | 'initialTarget'> & {
    uiEventContext: UIEventContext;
  }
) {
  const { canViewConversations, canViewUrgentMessaging, initialTarget, uiEventContext } = props;
  const initialTargets = initialTarget ? [initialTarget] : undefined;
  const skipUrgentMessagingDialog = Boolean(Cookies.get(SKIP_URGENT_MESSAGING_INFO_DIALOG_COOKIE));

  const urgentModal = skipUrgentMessagingDialog ? (
    <SplitComposerModal
      initialTargets={initialTargets}
      messageType="urgent"
      data-testid="urgent-composer-modal"
    />
  ) : (
    <UrgentMessagingLearnMoreDialog
      modal={<SplitComposerModal initialTargets={initialTargets} messageType="urgent" />}
      onRequestHide={() => {}}
      showCheckboxToSkip
      data-testid="urgent-learn-more-modal"
    />
  );

  return (
    <Menu width="medium" rowHover>
      <ModalMenuItem
        modal={<SplitComposerModal initialTargets={initialTargets} />}
        eventProperties={uiEventContext}
        eventKey="universal_composer.message_type_menu.menu_item.click"
        data-testid="composer-menu-item-normal-composer"
      >
        {canViewConversations ? (
          <MenuItem
            image={AnnouncementImage}
            title="Announcement"
            subtext="Sends separately; members cannot see other replies"
          />
        ) : (
          <MenuItem
            image={AnnouncementImage}
            title="Message"
            subtext="Announcements, group conversations, or individual conversation"
          />
        )}
      </ModalMenuItem>
      {canViewConversations && (
        <ModalMenuItem
          modal={<SplitComposerModal initialTargets={initialTargets} messageType="message" />}
          eventKey="universal_composer.message_type_menu.menu_item.click"
          eventProperties={uiEventContext}
          data-testid="composer-menu-item-conversation-composer"
        >
          <MenuItem
            image={ConversationImage}
            title="Conversation"
            subtext="Send as one group; members can see all replies"
          />
        </ModalMenuItem>
      )}
      {canViewUrgentMessaging && (
        <ModalMenuItem
          modal={urgentModal}
          eventKey="universal_composer.message_type_menu.menu_item.click"
          data-testid="composer-menu-item-urgent-composer"
          eventProperties={uiEventContext}
        >
          <MenuItem
            image={UrgentAnnouncementImage}
            title="Urgent Message"
            subtext="Override opt-out preferences and call recipients unreachable by text"
          />
        </ModalMenuItem>
      )}
    </Menu>
  );
}

export function getCommonButtonProps(options?: Pick<ButtonProps, 'disabled' | 'className'>) {
  return {
    disabled: options?.disabled ?? false,
    className: cx('TriggerComposerButton', options?.className),
    'data-test-selector': 'universal_compose_button',
    'data-testid': 'universal_compose_button',
    eventKey: 'universal_composer.universal_compose.click',
    eventElementName: 'UniversalComposeButton',
  };
}
